import { API_CONFIG } from '@/lib/api/config';
import { ApiError } from '@/lib/api/client';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';
import { scopedFetch } from '@/lib/api/scopedFetch';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export const cashfreeApi = {
  async connect(payload: {
    businessId: string;
    appId: string;
    secretKey: string;
    accountName?: string;
    webhookSecret?: string;
    environment?: 'sandbox' | 'production';
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to connect Cashfree');
    }
    clearConnectionCheckCache(`cashfree:${payload.businessId}`);
    return json;
  },

  async disconnect(businessId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to disconnect Cashfree');
    }
    clearConnectionCheckCache(`cashfree:${businessId}`);
    return json;
  },

  async checkConnection(businessId: string) {
    return cachedConnectionCheck(`cashfree:${businessId}`, async () => {
      const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/connection`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to check Cashfree connection');
      }
      return json;
    });
  },

  async testConnection(businessId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/test`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to test connection');
    }
    return json;
  },

  async createPaymentLink(businessId: string, payload: {
    amount: number;
    currency?: string;
    description?: string;
    customer?: {
      name?: string;
      email?: string;
      contact?: string;
    };
    notify?: {
      sms?: boolean;
      email?: boolean;
    };
    notes?: Record<string, any>;
    callback_url?: string;
  }) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/payment-links`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to create payment link');
    }
    return json;
  },

  async listPaymentLinks(businessId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.status) query.append('status', params.status);
    if (params?.search) query.append('search', params.search);
    const url = `${API_CONFIG.BASE_URL}/cashfree/${businessId}/payment-links${query.toString() ? `?${query.toString()}` : ''}`;
    const res = await scopedFetch(url, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to list payment links');
    }
    return json;
  },

  async getPaymentLink(businessId: string, linkId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/payment-links/${linkId}`, {
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to get payment link');
    }
    return json;
  },

  async cancelPaymentLink(businessId: string, linkId: string) {
    const res = await scopedFetch(`${API_CONFIG.BASE_URL}/cashfree/${businessId}/payment-links/${linkId}/cancel`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw ApiError.fromResult({ ...json, statusCode: res.status }, 'Failed to cancel payment link');
    }
    return json;
  },
};
